<script setup lang="ts">
import { Cloud, FileText, Library } from 'lucide-vue-next';
import SceneShell from '../SceneShell.vue';

// Two sources (Zotero + PDF) feed reference cards into the library.
const cards = [
    { from: 'top', d: '0s' },
    { from: 'bottom', d: '-1.4s' },
    { from: 'top', d: '-2.8s' },
    { from: 'bottom', d: '-4.2s' },
];
</script>

<template>
    <SceneShell>
        <div
            class="pointer-events-none absolute -top-16 left-1/2 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl"
        ></div>

        <!-- Converging belts -->
        <svg
            class="absolute inset-0 h-full w-full"
            viewBox="0 0 100 58"
            preserveAspectRatio="none"
            aria-hidden="true"
            fill="none"
        >
            <path
                d="M16 18 L80 29"
                stroke="rgba(56,189,248,0.35)"
                stroke-width="0.6"
                stroke-dasharray="2 2"
                class="dash"
            />
            <path
                d="M16 40 L80 29"
                stroke="rgba(56,189,248,0.35)"
                stroke-width="0.6"
                stroke-dasharray="2 2"
                class="dash"
            />
        </svg>

        <!-- Zotero source -->
        <div class="endpoint top-[31%] left-[14%]">
            <div class="node border-cyan-400/40 bg-cyan-400/10 text-cyan-200">
                <Cloud class="h-5 w-5" />
            </div>
            <p class="endpoint-label text-cyan-200">Zotero</p>
        </div>

        <!-- PDF source -->
        <div class="endpoint top-[69%] left-[14%]">
            <div
                class="node border-orange-400/40 bg-orange-400/10 text-orange-300"
            >
                <FileText class="h-5 w-5" />
            </div>
            <p class="endpoint-label text-orange-300">PDF</p>
        </div>

        <!-- Library -->
        <div class="endpoint top-1/2 left-[82%]">
            <div
                class="node h-14 w-14 border-cyan-400/50 bg-cyan-400/15 text-cyan-100 shadow-[0_0_26px_-4px_rgba(34,211,238,0.6)]"
            >
                <Library class="h-6 w-6" />
            </div>
            <p class="endpoint-label text-cyan-100">Bibliothèque</p>
        </div>

        <!-- Travelling reference cards -->
        <span
            v-for="(c, i) in cards"
            :key="i"
            class="ref-card"
            :class="c.from === 'top' ? 'ref-top' : 'ref-bottom'"
            :style="{ '--d': c.d }"
            aria-hidden="true"
        ></span>

        <p class="caption">Vos références réunies au même endroit.</p>
    </SceneShell>
</template>

<style scoped>
.endpoint {
    position: absolute;
    z-index: 10;
    transform: translate(-50%, -50%);
    text-align: center;
}

.node {
    display: flex;
    height: 3rem;
    width: 3rem;
    align-items: center;
    justify-content: center;
    border-radius: 1rem;
    border-width: 1px;
}

.endpoint-label {
    margin-top: 0.4rem;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
}

.ref-card {
    position: absolute;
    z-index: 8;
    height: 14px;
    width: 18px;
    border-radius: 3px;
    border: 1px solid rgba(56, 189, 248, 0.5);
    background: rgba(56, 189, 248, 0.15);
    transform: translate(-50%, -50%);
    opacity: 0;
}

.caption {
    position: absolute;
    bottom: 0.75rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 11px;
    color: rgb(100 116 139);
}

@media (prefers-reduced-motion: no-preference) {
    .dash {
        animation: dash-flow 1s linear infinite;
    }

    .ref-top {
        animation: ref-top 5.6s linear infinite;
        animation-delay: var(--d);
    }

    .ref-bottom {
        animation: ref-bottom 5.6s linear infinite;
        animation-delay: var(--d);
    }

    @keyframes dash-flow {
        to {
            stroke-dashoffset: -4;
        }
    }

    @keyframes ref-top {
        0% {
            left: 16%;
            top: 31%;
            opacity: 0;
        }
        10% {
            opacity: 1;
        }
        80% {
            left: 82%;
            top: 50%;
            opacity: 1;
        }
        92%,
        100% {
            left: 82%;
            top: 50%;
            opacity: 0;
        }
    }

    @keyframes ref-bottom {
        0% {
            left: 16%;
            top: 69%;
            opacity: 0;
        }
        10% {
            opacity: 1;
        }
        80% {
            left: 82%;
            top: 50%;
            opacity: 1;
        }
        92%,
        100% {
            left: 82%;
            top: 50%;
            opacity: 0;
        }
    }
}
</style>
